<template>
    <v-container class="pa-12">
        <v-card class="pl-16 pr-16 pt-8 pb-16">
            <v-card-title>
                <v-row>
                    <v-col cols="6" md="6">
                        <h3>Adjust Quotation / Fee Estimate</h3>
                    </v-col>
                    <v-col cols="6" md="6">
                        <v-row>
                            <v-col class="text-right" cols="12" md="12">
                                <h5>No. {{ doc.doc_no }}</h5>
                                <h5>Date {{ doc.doc_date_show }}</h5>
                                <v-chip x-small :color="statusColor" dark class="mt-1">{{ statusLabel }}</v-chip>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-btn-toggle v-model="docType" mandatory dense class="mb-4 ml-1 doctype-toggle doctype-toggle-locked">
                <v-btn small value="fixed" disabled>💰 Fixed Price (Quotation)</v-btn>
                <v-btn small value="estimate" disabled>⏱️ Estimate Fee</v-btn>
            </v-btn-toggle>

            <div v-if="docType === 'estimate'" class="wf-alert wf-alert-warn">
                ⚠️ เอกสารนี้เป็น <b>การประมาณการค่าบริการเบื้องต้น (Non-binding Estimate)</b>
                มิใช่ใบเสนอราคาที่มีผลผูกพันทางกฎหมาย
            </div>

            <!-- ===== Header fields: แก้ไขได้เต็มที่ (ยกเว้นข้อมูลลูกค้าที่ auto-fill) ===== -->
            <v-row class="row1 mt-3 pt-3">
                <v-col cols="12" md="6">
                    <v-text-field v-model="doc.client_code" label="Client Code" outlined dense
                        background-color="#F5F5F5" disabled></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field v-model="doc.client_name" label="Client Name" outlined dense
                        background-color="#F5F5F5" disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="10">
                    <v-text-field v-model="doc.client_address" label="Client Address" outlined dense></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" md="3">
                    <v-text-field v-model="doc.tax_id" label="Tax ID" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="doc.branch" label="Branch" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                    <v-text-field v-model="doc.payment_term" label="Payment Term (Day)" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="doc.currency" label="Currency" outlined dense background-color="#F5F5F5"
                        disabled></v-text-field>
                </v-col>
            </v-row>
            <v-row class="heightsize70formadvance">
                <v-col cols="12" sm="3">
                    <v-text-field v-model="doc.vat_percent" label="Vat rate (%)" outlined dense></v-text-field>
                </v-col>
                <v-col cols="6" md="3">
                    <v-text-field v-model="doc.attention" label="Attention" outlined dense></v-text-field>
                </v-col>
                <v-col cols="6" md="3">
                    <v-text-field v-model="doc.contact_position" label="Contact Position" outlined dense></v-text-field>
                </v-col>
            </v-row>
            <v-row class="row1 mt-3 pt-3">
                <v-col cols="12" md="12">
                    <v-textarea v-model="doc.additional_info" solo label="หมายเหตุ:" auto-grow rows="4"></v-textarea>
                </v-col>
            </v-row>

            <!-- ===== Fixed Price item table — แก้ไขได้เต็มที่ ===== -->
            <template v-if="docType === 'fixed'">
                <div class="wf-alert wf-alert-info mt-8">
                    <v-icon small color="#0d47a1">mdi-information-outline</v-icon>
                    VAT rate {{ Number(doc.vat_percent).toFixed(2) }}% จะใช้กับรายการที่เปิด 'คิด VAT' เท่านั้น
                </div>
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">รายการสินค้า/บริการ</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter" style="width:24px"></th>
                                <th class="texttablehcenter" style="width:36px">No.</th>
                                <th class="texttablehcenter" style="min-width:110px">ชื่อสินค้า/รายละเอียด</th>
                                <th class="texttablehcenter text-right" style="width:80px">จำนวน</th>
                                <th class="texttablehcenter" style="width:90px">หน่วย</th>
                                <th class="texttablehcenter text-right" style="width:150px">ราคาต่อหน่วย</th>
                                <th class="texttablehcenter text-right" style="width:130px">ราคาก่อน VAT</th>
                                <th class="texttablehcenter" style="width:160px">
                                    <div>คิด VAT</div>
                                    <v-checkbox :input-value="allFixedVatSelected"
                                        :indeterminate="fixedVatIndeterminate" dense hide-details dark
                                        class="ma-0 pa-0 d-inline-flex select-all-checkbox" label="เลือกทั้งหมด"
                                        @change="toggleAllVat('fixed', $event)"></v-checkbox>
                                </th>
                                <th class="texttablehcenter text-right" style="width:110px">VAT</th>
                                <th class="texttablehcenter text-right" style="width:130px">ราคารวม</th>
                                <th class="texttablehcenter" style="width:32px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in fixedRows" :key="row.id" :data-row-id="'fixed-' + row.id" draggable
                                @dragstart="onDragStart('fixed', row.id)"
                                @dragover.prevent="onDragOver('fixed', row.id)" @drop="onDrop('fixed', row.id)"
                                @dragend="onDragEnd" :class="dragRowClass('fixed', row.id)">
                                <td class="drag-handle">⇅</td>
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    <div class="item-picker">
                                        <v-combobox v-model="row.name" :items="filterMaster(row.searchText)"
                                            :search-input.sync="row.searchText" item-text="name" dense hide-details
                                            outlined placeholder="ชื่อสินค้า"
                                            @input="(val) => onNameInput('fixed', row, val)">
                                            <template v-slot:item="{ item }">
                                                <span
                                                    :class="isAddNewItem(item) ? 'blue--text font-weight-medium' : ''">{{
                                                        item }}</span>
                                            </template>
                                        </v-combobox>
                                    </div>
                                    <v-textarea v-model="row.detail"
                                        :placeholder="row.name ? 'รายละเอียด (Shift+Enter ขึ้นบรรทัดใหม่)' : 'กรุณาเลือกชื่อสินค้าก่อน'"
                                        dense hide-details rows="1" auto-grow solo flat class="detail-input mt-1"
                                        :disabled="!row.name"
                                        @keydown="onDetailKeydown($event, 'fixed', row)"></v-textarea>
                                </td>
                                <td class="text-right">
                                    <v-text-field v-model.number="row.qty" type="number" dense hide-details
                                        outlined></v-text-field>
                                </td>
                                <td>
                                    <v-text-field v-model="row.unit" dense hide-details outlined></v-text-field>
                                </td>
                                <td class="text-right">
                                    <v-text-field v-model.number="row.price" type="number" dense hide-details
                                        outlined></v-text-field>
                                </td>
                                <td class="text-right">{{ fmt(row.qty * row.price) }}</td>
                                <td class="text-center vat-switch-cell">
                                    <v-switch class="vat-switch" v-model="row.applyVat" dense hide-details inset
                                        :label="row.applyVat ? 'คิด VAT' : 'ไม่คิด VAT'" color="#1565C0"></v-switch>
                                </td>
                                <td class="text-right">{{ fmt(row.applyVat ? (row.qty * row.price) *
                                    (Number(doc.vat_percent) / 100) : 0) }}</td>
                                <td class="text-right font-weight-medium">
                                    {{ fmt(row.applyVat ? (row.qty * row.price) * (1 + Number(doc.vat_percent) / 100) :
                                        (row.qty * row.price)) }}
                                </td>
                                <td>
                                    <v-icon small color="error"
                                        @click="delRow('fixed', row.id)">mdi-delete-outline</v-icon>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
                <v-btn small color="#90CAF9" class="mt-2" @click="addRow('fixed')">➕ เพิ่มแถว</v-btn>

                <div class="d-flex justify-space-between align-center mt-3 mb-2 flex-wrap">
                    <span class="text-body-2 grey--text">
                        <v-icon small color="blue" class="mt-n1">mdi-information-outline</v-icon>
                        {{ fixedTotals.rowCount }} รายการ • คิด VAT {{ fixedTotals.vatCount }} • ไม่คิด VAT {{
                            fixedTotals.noVatCount }}
                    </span>
                </div>

                <v-row justify="end">
                    <v-col cols="12" md="5">
                        <v-card outlined class="summary-card pa-4">
                            <div class="summary-title font-weight-bold mb-3">สรุปยอด</div>

                            <div class="d-flex align-center summary-row mb-1">
                                <div class="summary-label">ยอดรวมก่อน VAT</div>
                                <div class="summary-value">{{ fmt(fixedTotals.total) }} {{ doc.currency }}</div>
                            </div>

                            <div class="d-flex align-center summary-row mb-1">
                                <div class="summary-label">รายการที่ติด VAT</div>
                                <div class="summary-value">{{ fmt(fixedTotals.vatBase) }} {{ doc.currency }}</div>
                            </div>

                            <div class="d-flex align-center summary-row mb-2">
                                <div class="summary-label">รายการที่ไม่ติด VAT</div>
                                <div class="summary-value">{{ fmt(fixedTotals.noVatBase) }} {{ doc.currency }}</div>
                            </div>

                            <v-divider class="mb-2"></v-divider>

                            <div class="d-flex align-center summary-row mb-2">
                                <div class="summary-label">VAT {{ Number(doc.vat_percent).toFixed(2) }}%</div>
                                <div class="summary-value">{{ fmt(fixedTotals.vat) }} {{ doc.currency }}</div>
                            </div>

                            <v-divider class="mb-2"></v-divider>

                            <div class="d-flex align-center summary-row summary-total-box">
                                <div class="summary-label font-weight-bold">ยอดสุทธิ</div>
                                <div class="summary-value text-h6 font-weight-bold primary--text">{{
                                    fmt(fixedTotals.grandTotal) }} {{
                                        doc.currency }}</div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </template>

            <!-- ===== Estimate Fee item table — แก้ไขได้เต็มที่ ===== -->
            <template v-else>
                <div class="wf-alert wf-alert-info mt-8">
                    <v-icon small color="#0d47a1">mdi-information-outline</v-icon>
                    VAT rate {{ Number(doc.vat_percent).toFixed(2) }}% จะใช้กับรายการที่เปิด 'คิด VAT' เท่านั้น
                </div>
                <div class="d-flex justify-space-between align-center mt-4 mb-1">
                    <h3 class="text-subtitle-1 mb-0">รายการบริการ (ประมาณการ)</h3>
                </div>
                <v-card outlined tile>
                    <v-simple-table dense>
                        <thead>
                            <tr class="bg-colorth">
                                <th class="texttablehcenter" style="width:24px"></th>
                                <th class="texttablehcenter" style="width:36px">No.</th>
                                <th class="texttablehcenter" style="min-width:260px">ชื่อสินค้า/รายละเอียด</th>
                                <th class="texttablehcenter text-right" style="width:180px">(Rate/Hr.) / Estimate Fees
                                </th>
                                <th class="texttablehcenter text-right" style="width:130px">ราคาก่อน VAT</th>
                                <th class="texttablehcenter" style="width:160px">
                                    <div>คิด VAT</div>
                                    <v-checkbox :input-value="allEstimateVatSelected"
                                        :indeterminate="estimateVatIndeterminate" dense hide-details dark
                                        class="ma-0 pa-0 d-inline-flex select-all-checkbox" label="เลือกทั้งหมด"
                                        @change="toggleAllVat('estimate', $event)"></v-checkbox>
                                </th>
                                <th class="texttablehcenter text-right" style="width:110px">VAT</th>
                                <th class="texttablehcenter text-right" style="width:130px">ราคารวม</th>
                                <th class="texttablehcenter" style="width:32px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in estimateRows" :key="row.id" :data-row-id="'estimate-' + row.id"
                                draggable @dragstart="onDragStart('estimate', row.id)"
                                @dragover.prevent="onDragOver('estimate', row.id)" @drop="onDrop('estimate', row.id)"
                                @dragend="onDragEnd" :class="dragRowClass('estimate', row.id)">
                                <td class="drag-handle">⇅</td>
                                <td>{{ idx + 1 }}</td>
                                <td>
                                    <div class="item-picker">
                                        <v-combobox v-model="row.name" :items="filterMaster(row.searchText)"
                                            :search-input.sync="row.searchText" item-text="name" dense hide-details
                                            outlined placeholder="ชื่อสินค้า/บริการ"
                                            @input="(val) => onNameInput('estimate', row, val)">
                                            <template v-slot:item="{ item }">
                                                <span
                                                    :class="isAddNewItem(item) ? 'blue--text font-weight-medium' : ''">{{
                                                        item }}</span>
                                            </template>
                                        </v-combobox>
                                    </div>
                                    <v-textarea v-model="row.detail"
                                        :placeholder="row.name ? 'รายละเอียด (Shift+Enter ขึ้นบรรทัดใหม่)' : 'กรุณาเลือกชื่อสินค้าก่อน'"
                                        dense hide-details rows="1" auto-grow solo flat class="detail-input mt-1"
                                        :disabled="!row.name"
                                        @keydown="onDetailKeydown($event, 'estimate', row)"></v-textarea>
                                </td>
                                <td class="text-right">
                                    <v-text-field v-model="row.rate" dense hide-details outlined
                                        type="number"></v-text-field>
                                </td>
                                <td class="text-right">{{ fmt(row.rate) }}</td>
                                <td class="text-center vat-switch-cell">
                                    <v-switch class="vat-switch" v-model="row.applyVat" dense hide-details inset
                                        :label="row.applyVat ? 'คิด VAT' : 'ไม่คิด VAT'" color="#1565C0"></v-switch>
                                </td>
                                <td class="text-right">{{ fmt(row.applyVat ? (Number(row.rate) || 0) *
                                    (Number(doc.vat_percent) / 100) : 0) }}</td>
                                <td class="text-right font-weight-medium">
                                    {{ fmt(row.applyVat ? (Number(row.rate) || 0) * (1 + Number(doc.vat_percent) / 100)
                                        :
                                        (Number(row.rate) || 0)) }}
                                </td>
                                <td>
                                    <v-icon small color="error"
                                        @click="delRow('estimate', row.id)">mdi-delete-outline</v-icon>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
                <v-btn small color="#90CAF9" class="mt-2" @click="addRow('estimate')">➕ เพิ่มแถว</v-btn>

                <div class="d-flex justify-space-between align-center mt-3 mb-2 flex-wrap">
                    <span class="text-body-2 grey--text">
                        <v-icon small color="blue" class="mt-n1">mdi-information-outline</v-icon>
                        {{ estimateTotals.rowCount }} รายการ • คิด VAT {{ estimateTotals.vatCount }} • ไม่คิด VAT {{
                            estimateTotals.noVatCount }}
                    </span>
                </div>

                <v-row justify="end">
                    <v-col cols="12" md="5">
                        <v-card outlined class="summary-card pa-4">
                            <div class="summary-title font-weight-bold mb-3">สรุปยอด</div>

                            <div class="d-flex align-center summary-row mb-1">
                                <div class="summary-label">ยอดรวมก่อน VAT</div>
                                <div class="summary-value">{{ fmt(estimateTotals.total) }} {{ doc.currency }}</div>
                            </div>

                            <div class="d-flex align-center summary-row mb-1">
                                <div class="summary-label">รายการที่ติด VAT</div>
                                <div class="summary-value">{{ fmt(estimateTotals.vatBase) }} {{ doc.currency }}</div>
                            </div>

                            <div class="d-flex align-center summary-row mb-2">
                                <div class="summary-label">รายการที่ไม่ติด VAT</div>
                                <div class="summary-value">{{ fmt(estimateTotals.noVatBase) }} {{ doc.currency }}</div>
                            </div>

                            <v-divider class="mb-2"></v-divider>

                            <div class="d-flex align-center summary-row mb-2">
                                <div class="summary-label">VAT {{ Number(doc.vat_percent).toFixed(2) }}%</div>
                                <div class="summary-value">{{ fmt(estimateTotals.vat) }} {{ doc.currency }}</div>
                            </div>

                            <v-divider class="mb-2"></v-divider>

                            <div class="d-flex align-center summary-row summary-total-box">
                                <div class="summary-label font-weight-bold">ยอดสุทธิ</div>
                                <div class="summary-value text-h6 font-weight-bold primary--text">{{
                                    fmt(estimateTotals.grandTotal) }}
                                    {{ doc.currency }}</div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row class="mt-4">
                    <v-col cols="12" md="6">
                        <v-text-field v-model="doc.not_to_exceed_cap"
                            label="วงเงินไม่เกิน (Not-to-Exceed Cap) — Optional" placeholder="ไม่ระบุ = ไม่จำกัดวงเงิน"
                            outlined dense></v-text-field>
                    </v-col>
                </v-row>
            </template>

            <v-row class="mt-2 mb-2">
                <v-col align="right" cols="12">
                    <v-btn outlined color="#1565C0" @click="onPrint">🖨️ Print / PDF</v-btn>
                </v-col>
            </v-row>

            <br />
            <hr /><br />

            <!-- ✅ ไม่มีปุ่ม Save Draft — มีแค่ปุ่ม Submit ตัวเดียว -->
            <div>
                <v-row>
                    <v-col align="center" cols="12" md="12">
                        <v-btn outlined class="mr-5" @click="getBackList()">Back</v-btn>
                        <v-btn depressed class="mr-5 buttonblue" @click="onSubmitAdjust">
                            Submit
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </v-card>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3500">{{ snackbar.text }}</v-snackbar>

        <add-product-dialog v-model="showAddProductDialog" :prefill-name="addProductPrefill"
            @saved="onProductSaved"></add-product-dialog>

        <v-dialog v-model="dialogSelectTitle" scrollable max-width="35%">
            <v-card class="pa-4">
                <v-card-text>
                    <div class="d-flex align-center mb-0 mt-5">
                        <span class="text-h5 font-weight-bold">Select Company</span>
                    </div>
                    <v-radio-group v-model="dialogSelectClientInfo" column>
                        <v-radio label="DEJ-UDOM & ASSOCIATES LIMITED" value="1" class="mb-2"></v-radio>
                        <v-radio label="สำนักงานกฎหมาย เดชอุดม แอนด์แอสโซซิเอทส์" value="3" class="mb-2"></v-radio>
                    </v-radio-group>
                </v-card-text>
                <v-card-actions style="margin-top: -30px;">
                    <v-row no-gutters>
                        <v-col cols="6" class="pr-2">
                            <v-btn color="#106a94" large rounded dark elevation="0" @click="confirmPrint" block>Print
                                Preview</v-btn>
                        </v-col>
                        <v-col cols="6" class="pl-2">
                            <v-btn color="#fc1526" large rounded dark elevation="0" @click="dialogSelectTitle = false"
                                block>Cancel</v-btn>
                        </v-col>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <PdfPreviewDialog v-model="dialogPDF" :blob="pdfBlob" :filename="getFileName()" />

        <!-- VAT detail dialog — เหมือนหน้า Form/View -->
        <v-dialog v-model="showVatDetailDialog" max-width="820px" scrollable content-class="vat-dialog-card">
            <v-card class="pa-6">
                <div class="d-flex justify-space-between align-start mb-1">
                    <div>
                        <div class="text-h5 font-weight-bold">รายละเอียดการคำนวณ VAT</div>
                        <div class="text-body-2 grey--text mt-1">คำนวณจากมูลค่ารายการก่อน VAT</div>
                    </div>
                    <v-btn icon @click="showVatDetailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <v-card outlined class="mt-4 vat-table-card">
                    <v-simple-table dense class="vat-detail-table">
                        <thead>
                            <tr>
                                <th>รายการ</th>
                                <th class="text-center">มูลค่าก่อน VAT<br />(THB)</th>
                                <th class="text-center">อัตรา VAT</th>
                                <th class="text-center">VAT<br />(THB)</th>
                                <th class="text-center">รวมหลัง VAT<br />(THB)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, idx) in vatDetailRows" :key="idx">
                                <td>{{ row.name }}</td>
                                <td class="text-right">{{ fmt(row.base) }}</td>
                                <td class="text-center">
                                    <v-chip v-if="row.applyVat" x-small color="#E3F2FD" text-color="#1565C0">{{
                                        row.vatRate
                                        }}%</v-chip>
                                    <span v-else class="grey--text text-caption">ไม่คิด VAT</span>
                                </td>
                                <td class="text-right">
                                    <div class="font-weight-medium">{{ fmt(row.vatAmount) }}</div>
                                    <div v-if="row.applyVat" class="text-caption grey--text">{{ fmt(row.base) }} × {{
                                        row.vatRate }}%</div>
                                </td>
                                <td class="text-right font-weight-medium">{{ fmt(row.afterVat) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
                <v-card outlined class="summary-card vat-summary-card pa-4 mt-4">
                    <div class="d-flex align-center summary-row mb-2">
                        <div class="summary-label">
                            <div>ยอดรายการทั้งหมด</div>
                            <div class="text-caption grey--text">รวมทุกแถวในตาราง</div>
                        </div>
                        <div class="summary-value">{{ fmt(vatDetailTotals.total) }} {{ doc.currency }}</div>
                    </div>
                    <div class="d-flex align-center summary-row mb-2">
                        <div class="summary-label">
                            <div>VAT รวม</div>
                            <div class="text-caption grey--text">เฉพาะรายการที่เลือกคิด VAT</div>
                        </div>
                        <div class="summary-value">{{ fmt(vatDetailTotals.vat) }} {{ doc.currency }}</div>
                    </div>
                    <div class="d-flex align-center summary-row summary-total-box">
                        <div class="summary-label">
                            <div class="font-weight-bold">ยอดชำระทั้งหมด</div>
                            <div class="text-caption grey--text">รวม VAT แล้ว</div>
                        </div>
                        <div class="summary-value vat-grand-total font-weight-bold primary--text">{{
                            fmt(vatDetailTotals.grandTotal) }} {{ doc.currency }}</div>
                    </div>
                </v-card>
                <v-card-actions class="mt-4 px-0">
                    <v-spacer></v-spacer>
                    <v-btn color="#1565C0" dark rounded elevation="0" width="120"
                        @click="showVatDetailDialog = false">ปิด</v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import AddProductDialog from "./AddProductDialog.vue";
import Swal from "sweetalert2";
import pdfQuotationGenerator from "@/utils/pdfQuotationGenerator";
import PdfPreviewDialog from "@/components/cards/PdfPreviewDialog";

export default {
    name: "QuotationAdjust",
    components: { AddProductDialog, PdfPreviewDialog },
    data() {
        return {
            pdfBlob: null,
            dialogPDF: false,
            dialogSelectTitle: false,
            dialogSelectClientInfo: "1",
            showAddProductDialog: false,
            addProductPrefill: "",
            addProductTarget: null,
            showVatDetailDialog: false,
            vatDetailType: "fixed",
            rowSeq: 100,
            ADD_NEW_PREFIX: '+ เพิ่ม "',
            ADD_NEW_SUFFIX: '" เป็นสินค้าใหม่',
            dragSrc: null,
            dragOver: null,
            snackbar: { show: false, text: "", color: "success" },

            docType: "fixed",
            doc: {
                quotation_id: null,
                doc_no: "",
                status: "",
                doc_date_show: "",
                client_id: 0,
                client_code: "",
                client_name: "",
                client_address: "",
                tax_id: "",
                branch: "",
                payment_term: "0",
                currency: "THB",
                vat_percent: "7.00",
                attention: "",
                contact_position: "",
                additional_info: "",
                not_to_exceed_cap: "",
            },

            masterItems: [],
            fixedRows: [],
            estimateRows: [],

            print_header_list: [
                {
                    id: "1", com_eng_1: "DEJ-UDOM & ASSOCIATES LTD.", com_eng_2: "ATTORNEYS AT LAW",
                    com_thai: "บริษัท เดชอุดม แอนด์ แอสโซซิเอทส์ จำกัด",
                    com_add_name: "CHARN ISSARA TOWER 1, 2nd FLOOR,", com_add_no: "942/69 RAMA IV ROAD,",
                    com_add_city: "KWAENG SURIYAWONG, KHET BANGRAK,", com_add_country: "BANGKOK 10500, THAILAND",
                    com_add_tel: "Tel : 0-2233-0055, 0-2267-5152", com_add_email: "E-MAIL : account@dejudom.com",
                    com_add_tax_id: `TAX ID. No. 0105534015348/"HEAD OFFICE"`,
                    com_display_name: "Dej-Udom & Associates Ltd.",
                },
                {
                    id: "3", com_eng_1: "DEJ-UDOM & ASSOCIATES", com_eng_2: "ATTORNEYS AT LAW",
                    com_thai: "สำนักกฎหมาย เดชอุดม แอนด์ แอสโซซิเอทส์",
                    com_add_name: "CHARN ISSARA TOWER 1, 2nd FLOOR,", com_add_no: "942/69 RAMA IV ROAD,",
                    com_add_city: "KWAENG SURIYAWONG, KHET BANGRAK,", com_add_country: "BANGKOK 10500, THAILAND",
                    com_add_tel: "Tel : 0-2233-0055, 0-2267-5152", com_add_email: "E-MAIL : account@dejudom.com",
                    com_add_tax_id: `TAX ID. No. 0992000028227/"HEAD OFFICE"`,
                    com_display_name: "Dej-Udom & Associates",
                },
            ],
            clientOptions: [],
        };
    },
    computed: {
        statusLabel() {
            return { Submitted: "Submitted", InProgress: "In Progress" }[this.doc.status] || this.doc.status;
        },
        statusColor() {
            return { Submitted: "blue", InProgress: "purple" }[this.doc.status] || "grey";
        },
        fixedTotals() {
            let vatBase = 0, noVatBase = 0, vatAmount = 0, vatCount = 0, noVatCount = 0;
            const rate = Number(this.doc.vat_percent) || 0;
            this.fixedRows.forEach((r) => {
                const lineTotal = (Number(r.qty) || 0) * (Number(r.price) || 0);
                if (r.applyVat) {
                    vatCount += 1;
                    vatBase += lineTotal;
                    vatAmount += lineTotal * (rate / 100);
                } else {
                    noVatCount += 1;
                    noVatBase += lineTotal;
                }
            });
            const total = vatBase + noVatBase;
            return {
                total, vat: vatAmount, grandTotal: total + vatAmount,
                vatBase, noVatBase, vatCount, noVatCount, rowCount: this.fixedRows.length,
            };
        },
        estimateTotals() {
            let vatBase = 0, noVatBase = 0, vatAmount = 0, vatCount = 0, noVatCount = 0;
            const rate = Number(this.doc.vat_percent) || 0;
            this.estimateRows.forEach((r) => {
                const lineTotal = Number(r.rate) || 0;
                if (r.applyVat) {
                    vatCount += 1;
                    vatBase += lineTotal;
                    vatAmount += lineTotal * (rate / 100);
                } else {
                    noVatCount += 1;
                    noVatBase += lineTotal;
                }
            });
            const total = vatBase + noVatBase;
            return {
                total, vat: vatAmount, grandTotal: total + vatAmount,
                vatBase, noVatBase, vatCount, noVatCount, rowCount: this.estimateRows.length,
            };
        },
        allFixedVatSelected() {
            return this.fixedRows.length > 0 && this.fixedRows.every((r) => r.applyVat);
        },
        fixedVatIndeterminate() {
            const checkedCount = this.fixedRows.filter((r) => r.applyVat).length;
            return checkedCount > 0 && checkedCount < this.fixedRows.length;
        },
        allEstimateVatSelected() {
            return this.estimateRows.length > 0 && this.estimateRows.every((r) => r.applyVat);
        },
        estimateVatIndeterminate() {
            const checkedCount = this.estimateRows.filter((r) => r.applyVat).length;
            return checkedCount > 0 && checkedCount < this.estimateRows.length;
        },
        vatDetailRows() {
            const rows = this.vatDetailType === "fixed" ? this.fixedRows : this.estimateRows;
            return rows.map((row) => {
                const base = this.vatDetailType === "fixed"
                    ? (Number(row.qty) || 0) * (Number(row.price) || 0)
                    : (Number(row.rate) || 0);
                const vatAmount = row.applyVat ? base * ((Number(row.vatRate) || 0) / 100) : 0;
                return { name: row.name || "-", base, applyVat: row.applyVat, vatRate: row.vatRate, vatAmount, afterVat: base + vatAmount };
            });
        },
        vatDetailTotals() {
            return this.vatDetailType === "fixed" ? this.fixedTotals : this.estimateTotals;
        },
    },
    async mounted() {
        try {
            await api.checkVersion();
            this.userId = localStorage.getItem(server.USER_ID);
            this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

            if (this.authorize_id == null || this.authorize_id == 0) {
                this.$router.back();
                return;
            }

            const parser = document.createElement("a");
            parser.href = window.location;
            const routerPath = parser.pathname.split("/")[1];
            const resAuth = await api.getAuthorize(this.userId, routerPath);
            const permission = resAuth.data[0];
            if (!permission || permission.smd_edit < 1) {
                this.$router.back();
                return;
            }

            if (!(this.userId && api.isLoggedIn())) {
                this.$store.state.isLogged = false;
                this.$router.push("/login");
                return;
            }
        } catch (error) {
            console.error("QuotationAdjust mounted() auth error:", error);
            this.$store.state.isLogged = false;
            this.$router.push("/login");
            return;
        }

        this.$showLoader();
        try {
            await this.loadCustomer();
            await this.loadMaterProductItem();
            await this.loadQuotationForAdjust(this.$route.params.id);
        } catch (error) {
            console.error("QuotationAdjust mounted() load data error:", error);
            await Swal.fire({
                icon: "error", title: "เกิดข้อผิดพลาด", text: "ไม่สามารถโหลดข้อมูลเอกสารได้ กรุณาลองใหม่อีกครั้ง",
                showConfirmButton: false, timer: 2500, timerProgressBar: true,
            });
        } finally {
            this.$hideLoader();
        }
    },
    methods: {
        async loadCustomer() {
            const result = await api.getAllCustomerListactiveCompany({
                params: { company_id: localStorage.getItem(server.COMPANYID) },
            });
            this.clientOptions = result.data.map((item) => ({
                id: item.id,
                code: item.cust_account,
                name: item.name,
                tax_id: item.taxid,
                branch: item.branch,
            }));
        },
        openVatDetail(type) {
            this.vatDetailType = type;
            this.showVatDetailDialog = true;
        },
        toggleAllVat(type, checked) {
            const rows = type === "fixed" ? this.fixedRows : this.estimateRows;
            rows.forEach((r) => { r.applyVat = checked; });
        },
        getFileName() {
            return `Quotation_${this.doc.doc_no || "Adjust"}.pdf`;
        },
        async loadMaterProductItem() {
            const res = await api.billing_expense_getInvTitleByDep(localStorage.getItem(server.DEPARTMENT_ID));
            this.masterItems = res.data.map((item) => ({ name: item.title, unit: item.unit, rate: Number(item.price) }));
        },
        // ✅ หัวใจของหน้านี้ — โหลดเอกสารแล้วเช็ค status ต้องเป็น Submitted หรือ InProgress เท่านั้น
        async loadQuotationForAdjust(id) {
            const res = await api.billing_expense_getAllQuotationsByID(id);
            const record = res.data;

            if (!record) {
                await Swal.fire({
                    icon: "error", title: "ไม่พบเอกสาร", text: "ไม่พบเอกสารที่ต้องการปรับปรุง",
                    showConfirmButton: false, timer: 2500, timerProgressBar: true,
                });
                this.$router.push("/quotation-list");
                return;
            }

            // ✅ guard สำคัญ: หน้านี้ใช้ได้เฉพาะ Submitted / InProgress เท่านั้น
            if (record.status !== "Submitted" && record.status !== "InProgress") {
                await Swal.fire({
                    icon: "warning", title: "ไม่สามารถปรับปรุงเอกสารนี้ได้",
                    text: "หน้านี้ใช้ได้เฉพาะเอกสารสถานะ Submitted หรือ In Progress เท่านั้น",
                    showConfirmButton: false, timer: 2800, timerProgressBar: true,
                });
                this.$router.push("/quotation-list");
                return;
            }

            // ✅ แทนที่ด้วย: ค้นหาจาก clientOptions
            const clientMatch = this.clientOptions.find(
                (c) => Number(c.id) === Number(record.client_id)
            );

            this.doc = {
                quotation_id: record.quotation_id,
                doc_no: record.doc_no,
                status: record.status,
                doc_date_show: this.formatDocDateShow(record.doc_date),
                client_id: record.client_id,
                client_code: clientMatch ? clientMatch.code : "",
                client_name: clientMatch ? clientMatch.name : (record.client ? record.client.name : `Client #${record.client_id}`),
                client_address: record.client_address,
                tax_id: clientMatch ? clientMatch.tax_id : "",
                branch: clientMatch ? clientMatch.branch : "",
                payment_term: String(record.payment_term ?? "0"),
                currency: record.currency,
                vat_percent: String(record.vat_percent ?? "0"),
                attention: record.attention,
                contact_position: record.contact_position,
                additional_info: record.additional_info,
                not_to_exceed_cap: record.not_to_exceed_cap != null ? String(record.not_to_exceed_cap) : "",
            };

            this.docType = record.doc_type;

            const items = record.items || [];
            if (this.docType === "fixed") {
                this.fixedRows = items.map((item) => ({
                    id: item.item_row_id, name: item.item_name, searchText: "", detail: item.detail || "",
                    qty: item.qty != null ? Number(item.qty) : 0, unit: item.unit || "",
                    price: item.unit_price != null ? Number(item.unit_price) : 0,
                    applyVat: item.apply_vat != null ? !!item.apply_vat : true,
                }));
                this.estimateRows = [];
            } else {
                this.estimateRows = items.map((item) => ({
                    id: item.item_row_id, name: item.item_name, searchText: "", detail: item.detail || "",
                    rate: item.line_total != null ? Number(item.line_total) : "",
                    applyVat: item.apply_vat != null ? !!item.apply_vat : true,
                }));
                this.fixedRows = [];
            }

            const maxId = Math.max(0, ...items.map((i) => i.item_row_id || 0));
            this.rowSeq = Math.max(this.rowSeq, maxId);
        },
        formatDocDateShow(dateOnly) {
            if (!dateOnly) return "";
            const [yyyy, mm, dd] = dateOnly.split("-");
            if (!yyyy || !mm || !dd) return "";
            return `${dd}/${mm}/${yyyy}`;
        },
        fmt(n) {
            return (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        getBackList() {
            this.$router.push("/quotation-list");
        },
        filterMaster(text) {
            const t = (text || "").toString().trim().toLowerCase();
            const original = (text || "").toString().trim();
            if (!t) return this.masterItems.map((m) => m.name);
            const matches = this.masterItems.filter((m) => m.name.toLowerCase().includes(t)).map((m) => m.name);
            if (matches.length === 0 && original) return [`${this.ADD_NEW_PREFIX}${original}${this.ADD_NEW_SUFFIX}`];
            return matches;
        },
        isAddNewItem(item) {
            return typeof item === "string" && item.startsWith(this.ADD_NEW_PREFIX) && item.endsWith(this.ADD_NEW_SUFFIX);
        },
        onNameInput(type, row, val) {
            if (typeof val === "string" && val.startsWith(this.ADD_NEW_PREFIX) && val.endsWith(this.ADD_NEW_SUFFIX)) {
                const typed = val.slice(this.ADD_NEW_PREFIX.length, val.length - this.ADD_NEW_SUFFIX.length);
                row.name = "";
                this.openAddProduct(type, row, typed);
                return;
            }
            row.name = val;
            const found = this.masterItems.find((m) => m.name === val);
            if (found) {
                if (type === "fixed") { row.unit = found.unit; row.price = found.rate; }
                else { row.rate = found.rate; }
            }
        },
        onDetailKeydown(e, type, row) {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const arr = type === "fixed" ? this.fixedRows : this.estimateRows;
                const idx = arr.findIndex((r) => r.id === row.id);
                const next = arr[idx + 1];
                if (next) {
                    this.$nextTick(() => {
                        const el = document.querySelector(`tr[data-row-id="${type}-${next.id}"] .item-picker input`);
                        if (el) el.focus();
                    });
                } else { this.addRow(type); }
            }
        },
        addRow(type) {
            this.rowSeq += 1;
            if (type === "fixed") {
                this.fixedRows.push({ id: this.rowSeq, name: "", searchText: "", detail: "", qty: 1, unit: "", price: 0, applyVat: true });
            } else {
                this.estimateRows.push({ id: this.rowSeq, name: "", searchText: "", detail: "", rate: "", applyVat: true });
            }
        },
        delRow(type, id) {
            if (type === "fixed") this.fixedRows = this.fixedRows.filter((r) => r.id !== id);
            else this.estimateRows = this.estimateRows.filter((r) => r.id !== id);
        },
        openAddProduct(type, row, prefill) {
            this.addProductTarget = { type, row };
            this.addProductPrefill = prefill || "";
            this.showAddProductDialog = true;
        },
        async onProductSaved(product) {
            if (this.addProductTarget) {
                const { type, row } = this.addProductTarget;
                row.name = product.name;
                row.searchText = product.name;
                if (type === "fixed") { row.unit = product.unit; row.price = product.rate; }
                else { row.rate = product.rate; }
            }
            this.addProductTarget = null;
            try { await this.loadMaterProductItem(); } catch (error) { console.error("loadMaterProductItem error:", error); }
        },
        onDragStart(type, id) { this.dragSrc = { type, id }; },
        onDragOver(type, id) { if (!this.dragSrc || this.dragSrc.type !== type) return; this.dragOver = id; },
        onDrop(type, id) {
            if (!this.dragSrc || this.dragSrc.type !== type || this.dragSrc.id === id) return;
            const arr = type === "fixed" ? this.fixedRows : this.estimateRows;
            const fromIdx = arr.findIndex((r) => r.id === this.dragSrc.id);
            const toIdx = arr.findIndex((r) => r.id === id);
            const [moved] = arr.splice(fromIdx, 1);
            arr.splice(toIdx, 0, moved);
            this.dragSrc = null; this.dragOver = null;
        },
        onDragEnd() { this.dragSrc = null; this.dragOver = null; },
        dragRowClass(type, id) {
            return this.dragOver === id && this.dragSrc && this.dragSrc.type === type ? "drop-target" : "";
        },
        toNullableNumber(val) {
            if (val === "" || val === undefined || val === null) return null;
            const n = Number(val);
            return isNaN(n) ? null : n;
        },
        toNullableString(val) { return val === "" || val === undefined ? null : val; },
        round2(num) { const n = Number(num) || 0; return Math.round((n + Number.EPSILON) * 100) / 100; },

        // ✅ Validation ก่อนบันทึก — เหมือนหน้า Form แต่ไม่เช็ค client_code เพราะเอกสารนี้ผูก client อยู่แล้ว
        validateBeforeSubmit() {
            const rows = this.docType === "fixed" ? this.fixedRows : this.estimateRows;
            if (rows.length === 0) {
                Swal.fire({
                    icon: "error", title: "เกิดข้อผิดพลาด", text: "กรุณาเพิ่มรายการอย่างน้อย 1 รายการ",
                    showConfirmButton: false, timer: 2500, timerProgressBar: true,
                });
                return false;
            }
            return true;
        },

        // ✅ กด Submit ในหน้านี้ — บันทึกข้อมูล แต่ "คง status เดิม" ไว้ ไม่ force เป็น Submitted
        // (เอกสาร InProgress ต้องยังเป็น InProgress ต่อไปหลังปรับปรุง ไม่ถอยสถานะกลับ)
        async onSubmitAdjust() {
            if (!this.validateBeforeSubmit()) return;

            const confirmResult = await Swal.fire({
                icon: "question",
                title: "ยืนยันการบันทึกการปรับปรุง",
                text: "ต้องการบันทึกการแก้ไขเอกสารนี้หรือไม่",
                showCancelButton: true,
                confirmButtonText: "บันทึก",
                cancelButtonText: "ยกเลิก",
            });
            if (!confirmResult.isConfirmed) return;

            var dataItem = {
                doc_type: this.docType,
                status: this.doc.status, // ✅ คง status เดิม (Submitted หรือ InProgress) ไม่เปลี่ยน
                modeUpdate: "updateData",
                client_id: Number(this.doc.client_id),
                client_address: this.doc.client_address,
                payment_term: this.doc.payment_term,
                currency: this.doc.currency,
                vat_percent: Number(this.doc.vat_percent),
                attention: this.doc.attention,
                contact_position: this.doc.contact_position,
                additional_info: this.doc.additional_info,
                subtotal: 0,
                vat_amount: 0,
                grand_total: 0,
                not_to_exceed_cap: 0,
                user_update: Number(localStorage.getItem(server.USER_ID)),
                company_id: Number(localStorage.getItem(server.COMPANYID)),
                department_id: Number(localStorage.getItem(server.DEPARTMENT_ID)),
            };

            if (this.docType === "fixed") {
                dataItem.subtotal = this.round2(this.fixedTotals.total);
                dataItem.vat_amount = this.round2(this.fixedTotals.vat);
                dataItem.grand_total = this.round2(this.fixedTotals.grandTotal);
            } else {
                dataItem.subtotal = this.round2(this.estimateTotals.total);
                dataItem.vat_amount = this.round2(this.estimateTotals.vat);
                dataItem.grand_total = this.round2(this.estimateTotals.grandTotal);
                dataItem.not_to_exceed_cap = Number(this.doc.not_to_exceed_cap);
            }

            const sourceRows = this.docType === "fixed" ? this.fixedRows : this.estimateRows;
            const itemsPayload = sourceRows.map((row, idx) => {
                const base = {
                    quotation_id: this.doc.quotation_id,
                    seq_no: idx + 1,
                    item_name: row.name || "",
                    detail: this.toNullableString(row.detail),
                    apply_vat: !!row.applyVat,
                };
                if (this.docType === "fixed") {
                    const qty = this.toNullableNumber(row.qty);
                    const unitPrice = this.toNullableNumber(row.price);
                    return { ...base, qty, unit: this.toNullableString(row.unit), unit_price: unitPrice, line_total: this.round2((qty || 0) * (unitPrice || 0)) };
                }
                return { ...base, qty: null, unit: null, unit_price: null, line_total: this.toNullableNumber(row.rate) };
            });

            this.$showLoader();
            try {
                const res_update = await api.billing_expense_updateQuotations(this.doc.quotation_id, dataItem);

                if (res_update.status == 200) {
                    await api.billing_expense_deleteQuotationsItem(this.doc.quotation_id);
                    if (itemsPayload.length > 0) {
                        await api.billing_expense_addQuotationsItem(itemsPayload);
                    }

                    await this.loadQuotationForAdjust(this.doc.quotation_id);
                    this.$hideLoader();

                    await Swal.fire({
                        icon: "success", title: "บันทึกสำเร็จ", text: "ปรับปรุงเอกสารเรียบร้อยแล้ว",
                        showConfirmButton: false, timer: 2000, timerProgressBar: true,
                    });
                }
            } catch (error) {
                console.error("onSubmitAdjust error:", error);
                this.$hideLoader();
                await Swal.fire({
                    icon: "error", title: "เกิดข้อผิดพลาด",
                    text: error.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
                    showConfirmButton: false, timer: 2500, timerProgressBar: true,
                });
            }
        },

        async onPrint() { this.dialogSelectTitle = true; },
        async confirmPrint() {
            try {
                const selectedCompany = this.print_header_list.find((c) => c.id === this.dialogSelectClientInfo) || this.print_header_list[0];
                const header = {
                    company_inv: "L",
                    com_eng_1: selectedCompany.com_eng_1, com_eng_2: selectedCompany.com_eng_2, com_thai: selectedCompany.com_thai,
                    com_add_name: selectedCompany.com_add_name, com_add_no: selectedCompany.com_add_no,
                    com_add_city: selectedCompany.com_add_city, com_add_country: selectedCompany.com_add_country,
                    com_add_tel: selectedCompany.com_add_tel, com_add_email: selectedCompany.com_add_email,
                    com_add_tax_id: selectedCompany.com_add_tax_id, company_display_name: selectedCompany.com_display_name,
                    doc_no: this.doc.doc_no, doc_type: this.docType,
                    customer_name: this.doc.client_name, customer_address: this.doc.client_address, customer_taxid: this.doc.tax_id,
                    attention: this.doc.attention, payment_term: this.doc.payment_term, currency: this.doc.currency,
                    subtotal: this.docType === "fixed" ? this.fixedTotals.total : this.estimateTotals.total,
                    vat_percent: this.doc.vat_percent,
                    vat_base: this.docType === "fixed" ? this.fixedTotals.vatBase : this.estimateTotals.vatBase,
                    no_vat_base: this.docType === "fixed" ? this.fixedTotals.noVatBase : this.estimateTotals.noVatBase,
                    vat_amount: this.docType === "fixed" ? this.fixedTotals.vat : this.estimateTotals.vat,
                    grand_total: this.docType === "fixed" ? this.fixedTotals.grandTotal : this.estimateTotals.grandTotal,
                    amount_in_words: "", not_to_exceed_cap: this.doc.not_to_exceed_cap, additional_info: this.doc.additional_info,
                };
                const sourceRows = this.docType === "fixed" ? this.fixedRows : this.estimateRows;
                const items = sourceRows.map((row) => ({
                    description: row.name, detail: row.detail,
                    apply_vat: !!row.applyVat,
                    amount: this.docType === "fixed" ? (row.qty || 0) * (row.price || 0) : undefined,
                    rate: this.docType === "estimate" ? this.fmt(row.rate) : undefined,
                }));
                const blob = await pdfQuotationGenerator.exportToPDF({ header, items });
                this.pdfBlob = blob;
                this.dialogSelectTitle = false;
                this.dialogPDF = true;
            } catch (error) {
                this.dialogSelectTitle = false;
                console.error("❌ เกิดข้อผิดพลาดในการสร้าง PDF:", error);
                alert("เกิดข้อผิดพลาดในการสร้าง PDF: " + error.message);
            }
        },
    },
    async beforeCreate() {
        this.$store.state.navMenu = true;
    },
};
</script>

<style scoped>
.bg-colorth {
    background-color: #595959;
}

.texttablehcenter {
    color: #fff !important;
    text-align: center;
}

.drag-handle {
    cursor: grab;
    color: #90a4ae;
    text-align: center;
}

.drop-target {
    box-shadow: inset 0 2px 0 #1565c0;
}

.detail-input>>>textarea {
    font-size: 12px;
    color: #1565c0;
}

.item-picker {
    position: relative;
}

.v-btn:not(.v-btn--round).v-size--default.buttonblue {
    background-color: #1d2442;
    color: white;
}

.heightsize70formadvance {
    height: 70px;
}

.wf-alert {
    border-radius: 6px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 14px;
}

.wf-alert-warn {
    background: #fff3e0;
    color: #e65100;
    border-left: 4px solid #ef6c00;
}

.wf-alert-info {
    background: #e3f2fd;
    color: #0d47a1;
    border-left: 4px solid #1565c0;
}

.select-all-checkbox {
    display: inline-flex;
}

.select-all-checkbox>>>label {
    color: #fff !important;
    font-size: 10px !important;
}

.vat-switch-cell {
    vertical-align: middle;
}

.vat-switch {
    margin-top: 0 !important;
    padding-top: 0 !important;
    display: flex;
    align-items: center;
}

.vat-switch>>>.v-input__slot {
    margin-bottom: 0;
}

.vat-switch>>>.v-messages {
    display: none;
}

.vat-switch>>>label {
    font-size: 11px !important;
    margin-left: 4px;
}

.summary-card {
    border-radius: 8px;
    background: #fff;
}

.summary-row {
    padding: 10px 12px;
    border-radius: 6px;
}

.summary-total-box {
    background: #e3f2fd;
}

.summary-label {
    flex: 1;
    min-width: 0;
}

.summary-value {
    flex-shrink: 0;
    text-align: right;
    white-space: nowrap;
    margin-left: 12px;
}

.summary-title {
    font-size: 25px;
    font-weight: 700;
    color: #1a1a1a;
    line-height: 1.3;
}

.vat-detail-link {
    color: #1565c0;
    font-size: 13px;
    text-decoration: underline;
    cursor: pointer;
}

.vat-detail-link:hover {
    color: #0d47a1;
}

.vat-dialog-card {
    border-radius: 16px;
}

.vat-table-card {
    border-radius: 8px;
    overflow: hidden;
}

.vat-detail-table {
    border-collapse: collapse;
}

.vat-detail-table th {
    font-weight: 600;
    color: #424242;
    background: #FAFAFA;
    font-size: 12.5px;
}

.vat-detail-table td {
    font-size: 13.5px;
}

.vat-detail-table th,
.vat-detail-table td {
    border-right: 1px solid #E0E0E0;
}

.vat-detail-table th:last-child,
.vat-detail-table td:last-child {
    border-right: none;
}

.vat-summary-card {
    font-size: 13px;
}

.vat-summary-card .summary-row {
    padding: 8px 10px;
}

.vat-grand-total {
    font-size: 18px;
}

.doctype-toggle-locked {
    pointer-events: none;
}

/* ✅ บังคับให้ปุ่มที่ active เห็นชัดแม้ตอน disabled (ปกติ Vuetify จะจางสีปุ่ม disabled ลง) */
.doctype-toggle-locked>>>.v-btn--active {
    background-color: #6a1b9a !important;
    color: #fff !important;
    opacity: 1 !important;
}

.doctype-toggle-locked>>>.v-btn--active::before {
    opacity: 0;
}

/* ปุ่มที่ไม่ถูกเลือก ให้จางลงเป็นสีเทาชัดเจนว่า "ปิดการใช้งาน" */
.doctype-toggle-locked>>>.v-btn:not(.v-btn--active) {
    opacity: 0.5;
}
</style>